import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import 'swiper/css';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import { Line, NonFormContainer } from 'components/form/FormLayout.styled';
import Card from 'components/global/Card';
import FormHeader from 'components/global/FormHeader';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import PrivacyPolicyModal from 'pages/Login/PrivacyPolicyModal';
import { FrequentlyAskedQuestion } from 'pages/Settings/Payment/FrequentlyAskedQuestion';

import { REFERRALS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faCoins, faExclamationSquare, faPeople } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Referrals.styled';

/**
 * @author @omerrkosar
 */

const Referrals = () => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					inviteData: yup.string().email().nullable(),
					referralLink: yup.string().nullable()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			inviteData: null,
			referralLink: null
		}
	});

	const { watch, setValue } = formMethods;

	const axiosPrivate = useAxiosPrivate();

	const [isLoading, setIsLoading] = useState(false);

	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const isTablet = useMediaQuery(Breakpoints.tablet);
	const showReferralLink = useSelector((state) => state.user.showReferLink);
	const isReferralBlocked = useSelector((state) => state.user.isReferralBlocked);
	const refereeAmount = useSelector((state) => state.user.localization.refereeAmount);
	const refererAmount = useSelector((state) => state.user.localization.refererAmount);

	const { showError, showSuccess } = useAlert();
	const [earnings, setEarnings] = useState({});
	const { Currency } = useLocale();

	const trackConfig = [
		{
			image: 'https://storage.googleapis.com/tryoto-public/referral/sendInvitation.svg',
			header: 'sendInvitation',
			content: 'sendInvitation.content'
		},
		{
			image: 'https://storage.googleapis.com/tryoto-public/referral/registration.svg',
			header: 'registration',
			content: 'registration.content'
		},
		{
			image: 'https://storage.googleapis.com/tryoto-public/referral/earnCredit.svg',
			header: 'earnCredit',
			content: 'earnCredit.content'
		}
	];
	const earningsConfig = [
		{
			icon: faPeople,
			header: 'totalReferrals',
			content: earnings.totalReferrals ?? 0,
			infoText: false,
			line: true,
			showCurrency: false
		},
		{
			icon: faCoins,
			header: 'totalReward',
			content: earnings.totalRewards ?? 0,
			infoText: false,
			line: true,
			showCurrency: true
		},
		{
			icon: faCoins,
			header: 'remainingReward',
			content: earnings.remainingBalance ?? 0,
			infoText: true,
			infoTextContent: 'referrals.remainingReward.infoText',
			line: true,
			showCurrency: true
		},
		{
			icon: faExclamationSquare,
			header: 'pendingReward',
			content: earnings.pendingRewards ?? 0,
			infoText: true,
			infoTextContent: 'referrals.pendingReward.infoText',
			line: false,
			showCurrency: true
		}
	];

	const defaultSort = useMemo(() => [{ id: 'lastUpdated', desc: true }], []);
	const earnColumns = useMemo(
		() => [
			{
				Header: 'recipientEmailAddress',
				accessor: 'recipientEmailAddress',
				width: 70,
				filterType: FilterType.string
			},
			{ Header: 'lastUpdated', accessor: 'lastUpdated', width: 50, filterType: FilterType.date },

			{
				Header: 'referral.status',
				accessor: 'status',
				width: 80,
				filterType: FilterType.multiSelection,
				renderCell: ListComponents.STATUS,
				showOnMobile: true
			}
		],
		[]
	);
	const getReferralLink = useCallback(() => {
		setIsLoading(true);
		axiosPrivate

			.post(REFERRALS.CREATE_REFERRAL_LINK)
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setValue('referralLink', data.referralLink ?? null);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			})
			.finally(() => setIsLoading(false));
	}, []);

	const getReferralEarning = useCallback(() => {
		setIsLoading(true);
		axiosPrivate

			.post(REFERRALS.GET_REFERRAL_EARNINGS)
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setEarnings(data);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			})
			.finally(() => setIsLoading(false));
	}, []);

	const inviteFriends = useCallback(() => {
		setIsLoading(true);
		axiosPrivate

			.post(REFERRALS.INVITE_FRIENDS, { emails: watch('inviteData') })
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					const failedEmails = data.failedEmails;
					const successfulEmails = data.successfulEmails;
					if (failedEmails?.length > 0) {
						showError({ header: 'invalidEmails', message: failedEmails.join(', ') });
						setIsLoading(false);
						setValue('inviteData', null);
					}
					if (successfulEmails?.length > 0) {
						showSuccess({ header: 'emailSent', message: successfulEmails.join(', ') });
						setValue('inviteData', null);
						setIsLoading(false);
					}
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({ header: 'referrals.email.couldntSent' });
				setIsLoading(false);
			});
	}, []);

	useEffect(() => {
		if (!isReferralBlocked && showReferralLink) {
			getReferralLink();
			getReferralEarning();
		}
	}, [isReferralBlocked, showReferralLink]);

	const socialMenuItems = [
		{
			name: 'email',
			url: 'mailto:?body=',
			image: 'https://storage.googleapis.com/tryoto-public/referral/mail.svg'
		},
		{
			name: 'WhatsApp',
			url: 'https://wa.me/?text=',
			image: 'https://storage.googleapis.com/tryoto-public/referral/whatsapp.svg'
		},
		{
			name: 'Facebook',
			url: 'https://www.facebook.com/sharer/sharer.php?u=',
			image: 'https://storage.googleapis.com/tryoto-public/referral/facebook.svg'
		},
		{
			name: 'Twitter',
			url: 'https://twitter.com/intent/tweet?text=',
			image: 'https://storage.googleapis.com/tryoto-public/referral/twitter.svg'
		}
		// {
		// 	name: 'Instagram',
		// 	url: 'https://www.instagram.com/?url=',
		// 	image: 'https://storage.googleapis.com/tryoto-public/referral/instagram.svg'
		// }
	];

	return (
		<S.PgContainer>
			<HeaderContainerFormHeader>
				<FormHeader boldTitle="referrals" />
			</HeaderContainerFormHeader>
			<Typography
				fontWeight="bold"
				variant="h2"
				className="mt-3"
				css={S.HeadingCss}
				noWrap
				style={{ overflow: 'unset' }}>
				referrals.header
			</Typography>
			<Typography noWrap align="center" css={S.HeadingCss} style={{ overflow: 'unset' }}>
				<Trans
					i18nKey="referrals.subtitle"
					variant="p1"
					color="var(--content-2)"
					components={{
						button: (
							<span
								style={{
									color: 'var(--blue)',
									textDecoration: 'underline',
									cursor: 'pointer'
								}}
								onClick={() => NiceModal.show(PrivacyPolicyModal)}></span>
						)
					}}
					values={{ refererAmount: `${refererAmount} ${Currency}` }}
				/>
			</Typography>

			{/* first section */}
			{!isMobile && (
				<NonFormContainer className="my-5">
					<S.RowCenterTopContainer>
						{trackConfig.map((item, index) => (
							<S.ColumnContainer key={index}>
								<Image url={item.image} width="11rem" height="11rem" />
								<Typography fontWeight="bold" variant="p1" className="my-3" noWrap>
									{item.header}
								</Typography>
								<Typography noWrap className="mb-3 mx-5 px-2 text-center">
									<Trans
										i18nKey={item.content}
										values={{
											refereeAmount: `${refereeAmount} ${Currency}`,
											refererAmount: `${refererAmount} ${Currency}`
										}}></Trans>
								</Typography>
							</S.ColumnContainer>
						))}

						{!isTablet && (
							<>
								<S.DottedLineContainer>
									<S.DottedLine />
								</S.DottedLineContainer>
								<S.DottedLineContainer2>
									<S.DottedLine />
								</S.DottedLineContainer2>
							</>
						)}
					</S.RowCenterTopContainer>
				</NonFormContainer>
			)}
			{isMobile && (
				<S.RowCenterTopContainer className="swiper-container m-3">
					<Swiper
						key={document.body.dir}
						dir={document.body.dir}
						spaceBetween={20}
						slidesOffsetAfter={40}
						pagination={{
							clickable: false
						}}
						slidesPerView="1.15"
						modules={[Autoplay, Pagination]}>
						{trackConfig.map((item, index) => (
							<SwiperSlide key={index}>
								<S.ColumnContainer key={index} className="swiper-slide">
									<Image url={item.image} width="13rem" height="13rem" className="mx-auto" />
									<Typography fontWeight="bold" variant="p1" className="pt-3 text-center" noWrap>
										{item.header}
									</Typography>
									<Typography noWrap className="p-5 text-center">
										<Trans
											i18nKey={item.content}
											values={{
												refereeAmount: `${refereeAmount} ${Currency}`,
												refererAmount: `${refererAmount} ${Currency}`
											}}></Trans>
									</Typography>
								</S.ColumnContainer>
							</SwiperSlide>
						))}
					</Swiper>
				</S.RowCenterTopContainer>
			)}

			{/* invite-share section */}
			<S.RowContainer>
				<S.ColumnContainer className="mobile">
					<Form formMethods={formMethods} style={{ flex: 1, flexDirection: 'column' }}>
						<S.ColumnContainer className="form-items">
							<Typography fontWeight="bold" variant="p1" className="py-3" noWrap>
								invite.title
							</Typography>
							<Typography noWrap className="pb-3">
								invite.description
							</Typography>
							<S.RowContainerInvite>
								<StringInput
									name="inviteData"
									disabled={!showReferralLink || isReferralBlocked}
									placeholder="emailComma"
								/>
								<Button
									loading={isLoading}
									label="send"
									onClick={inviteFriends}
									disabled={!showReferralLink || !watch('inviteData') || isReferralBlocked}
									disabledTooltip={
										!showReferralLink
											? 'referral.eligibility'
											: isReferralBlocked
												? 'referral.blocked'
												: 'referral.invite'
									}
								/>
							</S.RowContainerInvite>
						</S.ColumnContainer>
						<S.ColumnContainer className="form-items">
							<Typography
								fontWeight="bold"
								variant="p1"
								noWrap
								style={{ overflow: 'unset' }}
								className="py-3">
								share.title
							</Typography>
							<Typography noWrap style={{ overflow: 'unset' }} className="pb-3">
								share.description
							</Typography>
							<S.RowContainerShare>
								<CopyComponent
									// buttonDisabled={isReferralBlocked}
									disabledTooltip={isReferralBlocked ? 'referral.blocked' : 'referral.eligibility'}
									value={isReferralBlocked ? null : showReferralLink ? watch('referralLink') : null}
									toEmail={watch('inviteData')}
									className="copy-component"
								/>
								<S.RowEvenContainer className="social-image">
									{showReferralLink &&
										socialMenuItems.map((item, index) => (
											<Image
												key={index}
												url={item.image}
												width="5rem"
												height="5rem"
												onClick={() => window.open(item.url + watch('referralLink'), '_blank')}
											/>
										))}
								</S.RowEvenContainer>
							</S.RowContainerShare>
						</S.ColumnContainer>
					</Form>
				</S.ColumnContainer>

				{/* earnings section  */}
				<S.ColumnContainer className="mobile earnings">
					<Card padding="2" style={{ height: '100%', width: 'inherit' }}>
						<div>
							<Typography fontWeight="bold" variant="p1" className="my-4">
								myEarnings
							</Typography>
						</div>

						<Line />
						<div style={{ paddingBottom: '2.2rem' }}>
							{earningsConfig.map((item, index) => (
								<div key={index} className="my-1">
									<S.RowSpaceContainer>
										<S.RowCenterContainer>
											<Icon size="large" icon={item.icon} color="var(--black)" />

											<Typography fontWeight="500" className="mx-3">
												{item.header}
											</Typography>
											{item.infoText && (
												<Tooltip title={item.infoTextContent} placement="bottom">
													<Icon icon={faCircleInfo} color="var(--black)" />
												</Tooltip>
											)}
										</S.RowCenterContainer>

										<Typography
											fontWeight="800"
											withCurrency={item.showCurrency}
											currency={earnings?.currency}>
											{item.content}
										</Typography>
									</S.RowSpaceContainer>
									{item.line && <Line />}
								</div>
							))}
						</div>
					</Card>
				</S.ColumnContainer>
			</S.RowContainer>

			{/* table section */}
			<S.RowSpaceContainer className="mt-5 mb-4">
				<Typography fontWeight="bold" variant="p1" className="px-3">
					trackYourReferrals
				</Typography>
			</S.RowSpaceContainer>
			<S.TableContainer>
				<DynamicList
					id={DYNAMICLISTS.REFERRALS}
					defaultSort={defaultSort}
					columns={earnColumns}
					fetchUrl={REFERRALS.GET_REFERRALS_LIST}
					showHeader={true}
				/>
			</S.TableContainer>

			{/* last terms section */}
			<Typography fontWeight="bold" variant="p1" noWrap style={{ overflow: 'unset' }} className="p-3 mt-3">
				referralsFAQs
			</Typography>
			<NonFormContainer className="faqs">
				<FrequentlyAskedQuestion formName="referralsFAQ" />
			</NonFormContainer>
		</S.PgContainer>
	);
};

export default Referrals;
